import React, { useState } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../hooks/use-toast";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CreateSubscriptionModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_company: '',
    title: '',
    description: '',
    amount: ''
  });
  const [loading, setLoading] = useState(false);
  const { token } = useAuth();
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.client_name || !formData.client_email || !formData.title || !formData.amount) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/subscriptions`, {
        ...formData,
        amount: parseFloat(formData.amount)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: "Subscription Created",
        description: "You can now send the invitation to your client.",
      });

      onSuccess?.(response.data);
      onClose();
      setFormData({
        client_name: '',
        client_email: '',
        client_company: '',
        title: '',
        description: '',
        amount: ''
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to create subscription",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <RefreshCw className="w-5 h-5 text-[#e6007e]" />
            Create Subscription
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Client Details */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-gray-700 font-heading">Client Details</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-body">Client Name *</Label>
                <Input
                  value={formData.client_name}
                  onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                  placeholder="John Smith"
                  className="font-body"
                  data-testid="subscription-client-name"
                />
              </div>
              <div>
                <Label className="text-sm font-body">Client Email *</Label>
                <Input
                  type="email"
                  value={formData.client_email}
                  onChange={(e) => setFormData({...formData, client_email: e.target.value})}
                  placeholder="john@example.com"
                  className="font-body"
                  data-testid="subscription-client-email"
                />
              </div>
            </div>
            <div>
              <Label className="text-sm font-body">Company (Optional)</Label>
              <Input
                value={formData.client_company}
                onChange={(e) => setFormData({...formData, client_company: e.target.value})}
                placeholder="Acme Ltd"
                className="font-body"
                data-testid="subscription-client-company"
              />
            </div>
          </div>

          {/* Subscription Details */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-gray-700 font-heading">Subscription Details</h4>
            <div>
              <Label className="text-sm font-body">Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="Monthly Retainer - Web Maintenance"
                className="font-body"
                data-testid="subscription-title"
              />
            </div>
            <div>
              <Label className="text-sm font-body">Description (Optional)</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Describe the scope of work included in this subscription..."
                className="font-body resize-none"
                rows={3}
                data-testid="subscription-description"
              />
            </div>
            <div>
              <Label className="text-sm font-body">Monthly Amount (GBP) *</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">&pound;</span>
                <Input
                  type="number"
                  min="1"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({...formData, amount: e.target.value})}
                  placeholder="500.00"
                  className="pl-7 font-body"
                  data-testid="subscription-amount"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1 font-body">
                This amount will be charged monthly on the same day.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose} className="font-body">
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="bg-[#e6007e] hover:bg-[#c90068] text-white font-body"
              disabled={loading}
              data-testid="create-subscription-submit"
            >
              {loading ? "Creating..." : "Create Subscription"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateSubscriptionModal;
